import React, { useCallback, useMemo, useState } from 'react'
import { useScreenshotStepData } from '@/tiptap/hooks/useExtensionData'
import { Editor } from '@tiptap/core'
import { Fragment, Node as ProseMirrorNode } from '@tiptap/pm/model'
import { NodeViewContent, NodeViewWrapper } from '@tiptap/react'
import { getStepAction } from '@deepguide-ai/shared'
import { IconCategory, IconType } from '@deepguide-ai/types'
import { useAtom } from 'jotai'
import { UpgradeDialog } from '@/components/plan-restrictions/UpgradeDialog'
import { useFeatureAccess } from '@/hooks/useFeatureAccess'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { ImageViewerDialog } from '@/components/ui/image-viewer-dialog'
import { trackEvent } from '@/lib/posthog'
import { trpcClient } from '@/lib/trpc'
import { handleImageUpload } from '@/tiptap/lib/tiptap-utils'
import {
  Eye,
  Lock,
  MousePointerClick,
  Pencil,
  Trash2,
  X,
  ZoomInIcon,
  ZoomOutIcon,
  SquarePen,
} from 'lucide-react'
import { nanoid } from 'nanoid'
import { editedScreenshotMapAtom } from '@/stores/imageEditorStore'
import {
  selectedSopStepIdsAtom,
  sopStepSelectionModeAtom,
} from '@/stores/sopStepCopyStore'
import { cn } from '@/lib/utils'
import { EmojiPicker } from '@/components/emoji-picker'
import type {
  AnyShapeData,
  InteractionPointData,
} from '@deepguide-ai/react-image-editor'
// Lazy-loaded so the browser-only editor submodule stays out of the server
// bundle graph (this node view is pulled into `@glyde/web/tiptap-server` for
// markdown serialization, which the server evaluates at runtime).
const ImageEditor = React.lazy(() =>
  import('@deepguide-ai/react-image-editor').then((module) => ({
    default: module.ImageEditor,
  }))
)
import { toast } from 'sonner'
import {
  Button,
  IconComponent,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Separator,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Checkbox,
} from '@/components/ui'
import { InteractionPoint } from './screenshot-extension'
import { ScreenshotRenderer } from './shared/screenshot-renderer'
import {
  screenshotToolbarButtonClassName,
  screenshotToolbarClassName,
} from './shared/screenshot-toolbar-styles'
import { StepAttributes } from './step-extension'

const STEP_COLORS = ['#7C9070', '#5B9BD5', '#D4845E', '#9B8FCE', '#E0917B', '#6BA5A0']

// Screenshot Toolbar Component
const ScreenshotToolbar = ({
  zoomLevel,
  onZoomChange,
  onDelete,
  interactionPoints,
  src,
  stepId,
  canModify = false,
  persistToRecordedStep = true,
  onImageSaved,
}: {
  zoomLevel: number
  onZoomChange: (zoom: number) => void
  onDelete: () => void
  interactionPoints: InteractionPoint[]
  src: string
  stepId: string
  canModify?: boolean
  persistToRecordedStep?: boolean
  onImageSaved?: (
    url: string,
    viewportCoordinates?: {
      topLeft: { x: number; y: number }
      topRight: { x: number; y: number }
      bottomLeft: { x: number; y: number }
      bottomRight: { x: number; y: number }
      center: { x: number; y: number }
      width: number
      height: number
    }
  ) => void
}) => {
  const [isFullscreenOpen, setIsFullscreenOpen] = useState(false)
  const [isEditorOpen, setIsEditorOpen] = useState(false)
  const [isUpgradeOpen, setIsUpgradeOpen] = useState(false)
  // Image editor is a PRO/ENTERPRISE feature; lower plans see an upgrade prompt.
  // Only needed when the toolbar can actually modify the image — skip in read-only
  // views (public SOP viewer, PDF export) so they don't fire authenticated queries
  // that 401 without a session and trigger a global logout/redirect.
  const imageEditorAccess = useFeatureAccess('imageEditor', canModify)
  const [, setEditedMap] = useAtom(editedScreenshotMapAtom)

  const initialInteractionPoints = useMemo<InteractionPointData[]>(
    () =>
      interactionPoints?.map((point) => ({
        id: point.id,
        tool: 'interactionPoint',
        x: point.xPercent,
        y: point.yPercent,
        type:
          point.type === 'Click' ||
          point.type === 'Hover' ||
          point.type === 'Input' ||
          point.type === 'Submit' ||
          point.type === 'Scroll'
            ? point.type
            : 'Click',
      })) || [],
    [interactionPoints]
  )

  const handleSaveEditedImage = useCallback(
    async (
      imageData: Blob,
      annotations: AnyShapeData[],
      savedInteractionPoints: InteractionPointData[]
    ) => {
      try {
        const fileExtensionByMimeType: Record<string, string> = {
          'image/webp': 'webp',
          'image/png': 'png',
          'image/jpeg': 'jpg',
        }
        const fileExtension = fileExtensionByMimeType[imageData.type]
        if (!fileExtension) {
          throw new Error(`Unsupported edited image type: ${imageData.type}`)
        }

        const file = new File(
          [imageData],
          `edited-screenshot-${Date.now()}.${fileExtension}`,
          { type: imageData.type }
        )
        const url = await handleImageUpload(file)

        if (!persistToRecordedStep) {
          setEditedMap((prev) => ({ ...prev, [stepId]: { url } }))
          onImageSaved?.(url)
          toast.success('Image saved')
          setIsEditorOpen(false)
          return
        }

        await trpcClient.steps.saveEditedScreenshot.mutate({
          stepId,
          editedUrl: url,
          editedAnnotation: {
            annotations,
            interactionPoints: savedInteractionPoints,
          },
        })

        let nextViewportCoordinates:
          | Parameters<NonNullable<typeof onImageSaved>>[1]
          | undefined

        try {
          const { step } = await trpcClient.steps.getStepById.query({
            id: stepId,
          })
          const baseWidth =
            step?.viewportInfo?.width ?? step?.viewportCoordinates?.width
          const baseHeight =
            step?.viewportInfo?.height ?? step?.viewportCoordinates?.height

          if (
            baseWidth &&
            baseHeight &&
            savedInteractionPoints.length > 0
          ) {
            const latest =
              savedInteractionPoints[savedInteractionPoints.length - 1]
            const xPx = (latest.x / 100) * baseWidth
            const yPx = (latest.y / 100) * baseHeight
            const currentVC = step?.viewportCoordinates

            nextViewportCoordinates = currentVC
              ? { ...currentVC, center: { x: xPx, y: yPx } }
              : {
                  topLeft: { x: 0, y: 0 },
                  topRight: { x: baseWidth, y: 0 },
                  bottomLeft: { x: 0, y: baseHeight },
                  bottomRight: { x: baseWidth, y: baseHeight },
                  center: { x: xPx, y: yPx },
                  width: baseWidth,
                  height: baseHeight,
                }

            await trpcClient.steps.updateStep.mutate({
              id: stepId,
              viewportCoordinates: nextViewportCoordinates,
            })
          }
        } catch (error) {
          console.error('Failed to update screenshot interaction point', error)
        }

        setEditedMap((prev) => ({
          ...prev,
          [stepId]: nextViewportCoordinates
            ? { url, viewportCoordinates: nextViewportCoordinates }
            : { url },
        }))
        onImageSaved?.(url, nextViewportCoordinates)
        toast.success('Image saved')
        setIsEditorOpen(false)
      } catch (error) {
        console.error('Failed to save edited screenshot', error)
        toast.error('Image failed to save, please try later.')
      }
    },
    [onImageSaved, persistToRecordedStep, setEditedMap, stepId]
  )

  const trackScreenshotToolbarClick = useCallback(
    (action: string, properties?: Record<string, unknown>) => {
      trackEvent('doc.screenshot_toolbar.clicked', {
        action,
        stepId,
        zoomLevel,
        canModify,
        ...properties,
      })
    },
    [canModify, stepId, zoomLevel]
  )

  if (canModify) {
    return (
      <>
        <div
          className={screenshotToolbarClassName}
          contentEditable={false}
        >
          <button
            type='button'
            onClick={() => {
              const nextZoom = Math.max(1, zoomLevel - 0.25)
              trackScreenshotToolbarClick('zoom_out', { nextZoom })
              onZoomChange(nextZoom)
            }}
            title='Zoom out'
            disabled={zoomLevel <= 1}
            className={cn(
              screenshotToolbarButtonClassName,
              zoomLevel <= 1 && 'pointer-events-none opacity-50'
            )}
          >
            <ZoomOutIcon className='size-[18px]' />
          </button>
          <button
            type='button'
            onClick={() => {
              const nextZoom = Math.min(10, zoomLevel + 0.25)
              trackScreenshotToolbarClick('zoom_in', { nextZoom })
              onZoomChange(nextZoom)
            }}
            title='Zoom in'
            disabled={zoomLevel >= 10}
            className={cn(
              screenshotToolbarButtonClassName,
              zoomLevel >= 10 && 'pointer-events-none opacity-50'
            )}
          >
            <ZoomInIcon className='size-[18px]' />
          </button>
          <button
            type='button'
            onClick={() => {
              trackScreenshotToolbarClick('view_fullscreen')
              setIsFullscreenOpen(true)
            }}
            title='View fullscreen'
            className={screenshotToolbarButtonClassName}
          >
            <Eye className='size-[18px]' />
          </button>
          {!imageEditorAccess.loading &&
            (imageEditorAccess.enabled ? (
              <button
                type='button'
                onClick={() => {
                  trackScreenshotToolbarClick('edit_screenshot')
                  setIsEditorOpen(true)
                }}
                title='Edit screenshot'
                className={screenshotToolbarButtonClassName}
              >
                <SquarePen className='size-[18px]' />
              </button>
            ) : (
              <button
                type='button'
                onClick={() => {
                  trackScreenshotToolbarClick('edit_screenshot_locked')
                  setIsUpgradeOpen(true)
                }}
                title='Edit'
                className={cn(screenshotToolbarButtonClassName, 'relative')}
              >
                <SquarePen className='size-[18px]' />
                <span className='absolute -top-1 -right-1 inline-flex items-center justify-center rounded-full bg-white shadow-sm'>
                  <Lock className='size-3 p-[1px] text-muted-foreground' />
                </span>
              </button>
            ))}
          <button
            type='button'
            onClick={() => {
              trackScreenshotToolbarClick('remove_screenshot')
              onDelete()
            }}
            title='Remove screenshot'
            className={cn(
              screenshotToolbarButtonClassName,
              'hover:bg-red-50 hover:text-red-600'
            )}
          >
            <Trash2 className='size-[18px]' />
          </button>
        </div>
        <ImageViewerDialog
          open={Boolean(isFullscreenOpen)}
          imageUrl={src}
          onOpenChange={(open) => {
            if (!open) {
              setIsFullscreenOpen(false)
            }
          }}
        />
        <UpgradeDialog
          open={isUpgradeOpen}
          onOpenChange={setIsUpgradeOpen}
          featureName='the screenshot editor'
          featureDescription='Annotate, blur, and edit screenshots with the image editor — available on the Pro plan.'
          currentPlan={imageEditorAccess.currentPlan}
          requiredPlan={imageEditorAccess.requiredPlan ?? 'PRO'}
          source='step_node_toolbar'
        />
        <Dialog
          open={isEditorOpen}
          onOpenChange={(open) => {
            if (!open) {
              setIsEditorOpen(false)
            }
          }}
        >
          <DialogContent
            hideClose
            className='!top-0 !left-0 h-screen w-screen !max-w-none !translate-x-0 !translate-y-0 gap-0 overflow-hidden rounded-none border-0 bg-muted p-0 shadow-none'
          >
            <DialogTitle className='sr-only'>Edit image</DialogTitle>
            <React.Suspense fallback={null}>
              <ImageEditor
                className='glyde-image-editor'
                src={src}
                stepId={stepId}
                initialInteractionPoints={initialInteractionPoints}
                onCancel={() => setIsEditorOpen(false)}
                onSave={handleSaveEditedImage}
                onAnalyticsEvent={(event, properties) => {
                  trackEvent(event, {
                    source: 'step_node_toolbar',
                    stepId,
                    ...properties,
                  })
                }}
              />
            </React.Suspense>
          </DialogContent>
        </Dialog>
      </>
    )
  }

  return (
    <div
      className={screenshotToolbarClassName}
      contentEditable={false}
    >
      <button
        type='button'
        onClick={() => {
          const nextZoom = Math.max(1, zoomLevel - 0.25)
          trackScreenshotToolbarClick('zoom_out', { nextZoom })
          onZoomChange(nextZoom)
        }}
        title='Zoom out'
        disabled={zoomLevel <= 1}
        className={cn(
          screenshotToolbarButtonClassName,
          zoomLevel <= 1 && 'pointer-events-none opacity-50'
        )}
      >
        <ZoomOutIcon className='size-[18px]' />
      </button>
      <button
        type='button'
        onClick={() => {
          const nextZoom = Math.min(10, zoomLevel + 0.25)
          trackScreenshotToolbarClick('zoom_in', { nextZoom })
          onZoomChange(nextZoom)
        }}
        title='Zoom in'
        disabled={zoomLevel >= 10}
        className={cn(
          screenshotToolbarButtonClassName,
          zoomLevel >= 10 && 'pointer-events-none opacity-50'
        )}
      >
        <ZoomInIcon className='size-[18px]' />
      </button>
    </div>
  )
}

interface StepNodeViewProps {
  node: ProseMirrorNode
  editor: Editor
  getPos: () => number | undefined
  updateAttributes: (attributes: Record<string, any>) => void
  deleteNode: () => void
  selected: boolean
}

export function StepNodeView({
  node,
  editor,
  getPos,
  updateAttributes,
  deleteNode,
  selected,
}: StepNodeViewProps) {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const [, setEditableVersion] = useState(0)
  const [isEditingTitle, setIsEditingTitle] = useState(false)
  const [isSelectionMode] = useAtom(sopStepSelectionModeAtom)
  const [selectedStepIds, setSelectedStepIds] = useAtom(selectedSopStepIdsAtom)

  React.useEffect(() => {
    const handleEditableChange = () => {
      setEditableVersion((version) => version + 1)
    }

    ;(editor as any).on('editableChange', handleEditableChange)
    return () => {
      ;(editor as any).off('editableChange', handleEditableChange)
    }
  }, [editor])

  const attrs = node.attrs as StepAttributes
  const isDetached = Boolean(attrs.detachedFromRecording)
  const stepTitle = attrs.title || ''
  const guideAction = attrs.guideAction || ''
  const showTitle = attrs.showTitle // Default to false
  const showCaption = attrs.showCaption || false // Default to false
  const icon = attrs.icon || 'sparkles'
  const iconType = (attrs.iconType || 'REGULAR') as IconType
  const iconCategory = (attrs.iconCategory || 'LUCIDE') as IconCategory
  const iconColor = attrs.iconColor || '#3b82f6'
  const showIcon = attrs.showIcon || false
  const cropStyle = attrs.cropStyle || 'cropped'

  const [editingTitle, setEditingTitle] = useState(stepTitle)
  // Temporary state for popover editing
  const [tempTitle, setTempTitle] = useState(stepTitle)
  const [tempGuideAction, setTempGuideAction] = useState(guideAction)
  const [tempShowTitle, setTempShowTitle] = useState(showTitle)
  const [tempIcon, setTempIcon] = useState(icon)
  const [tempIconType, setTempIconType] = useState(iconType)
  const [tempIconCategory, setTempIconCategory] = useState(iconCategory)
  const [tempIconColor, setTempIconColor] = useState(iconColor)
  const [tempShowIcon, setTempShowIcon] = useState(showIcon)
  const [tempCropStyle, setTempCropStyle] = useState(cropStyle)
  const [_tempScreenshotZoomLevel, setTempScreenshotZoomLevel] = useState(
    attrs.screenshotZoomLevel || 1
  )

  // Update editing title when attrs.title changes
  React.useEffect(() => {
    if (!isEditingTitle) {
      setEditingTitle(stepTitle)
    }
  }, [stepTitle, isEditingTitle])

  // Calculate step number based on position within parent Steps container.
  // Pure — must not write to the document here. `updateAttributes` inside a
  // useMemo synchronously dispatches a ProseMirror transaction while `Step`
  // is still rendering, which React flags ("Cannot update a component while
  // rendering a different component") and which cascades into a runaway
  // re-render loop across every Step in the doc — pegs the tab's CPU and
  // stalls Puppeteer PDF capture on step-heavy documents. The correction is
  // applied in the effect below instead, after render.
  const stepNumber = useMemo(() => {
    try {
      const pos = getPos()
      if (pos === undefined) return attrs.number || 1

      const $pos = editor.state.doc.resolve(pos)
      let stepsNode: ProseMirrorNode | null = null
      let stepsPos = 0

      // Walk up the tree to find the Steps parent
      for (let depth = $pos.depth; depth > 0; depth--) {
        const node = $pos.node(depth)
        if (node.type.name === 'Steps') {
          stepsNode = node
          stepsPos = $pos.before(depth)
          break
        }
      }

      if (!stepsNode) return attrs.number || 1

      // Count Step nodes within the Steps container
      let stepCount = 0
      stepsNode.forEach((child, offset) => {
        const childPos = stepsPos + offset + 1
        if (child.type.name === 'Step' && childPos < pos) {
          stepCount++
        }
      })

      return stepCount + 1
    } catch (error) {
      console.error('Error calculating step number', error)
      return attrs.number || 1
    }
  }, [editor.state.doc, getPos, attrs.number])

  React.useEffect(() => {
    if (stepNumber !== attrs.number) {
      updateAttributes({ number: stepNumber })
    }
  }, [stepNumber, attrs.number, updateAttributes])

  const totalSteps = useMemo(() => {
    try {
      const pos = getPos()
      if (pos === undefined) return 1

      const $pos = editor.state.doc.resolve(pos)
      for (let depth = $pos.depth; depth > 0; depth--) {
        const parent = $pos.node(depth)
        if (parent.type.name === 'Steps') return parent.childCount
      }
    } catch (error) {
      console.error('Error calculating total steps', error)
    }
    return 1
  }, [editor.state.doc, getPos])
  const [tempPosition, setTempPosition] = useState(stepNumber)

  // Extract caption text from first paragraph in node content
  // NOTE: Must be after stepNumber calculation since it depends on stepNumber
  const captionText = useMemo(() => {
    if (!showCaption) {
      return '';
    }

    if (attrs.title) {
      return attrs.title;
    }

    if (!node.content || node.content.size === 0) {
      return '';
    }

    // Find first paragraph node
    let firstParagraphText = '';
    node.content.forEach((child) => {
      if (child.type.name === 'paragraph' && !firstParagraphText) {
        // Extract text content from paragraph
        child.content?.forEach((textNode) => {
          if (textNode.type.name === 'text' && textNode.text) {
            firstParagraphText += textNode.text;
          }
        });
      }
    });

    // Limit to 150 characters
    const caption = firstParagraphText.substring(0, 150);
    return caption;
  }, [node.content, showCaption, attrs.title]);

  // Function to save settings from popover
  const saveSettings = useCallback(() => {
    const newAttributes: Partial<StepAttributes> = {}

    if (tempTitle.trim() !== stepTitle)
      newAttributes.title = tempTitle.trim() || ''
    if (tempGuideAction.trim() !== guideAction)
      newAttributes.guideAction = tempGuideAction.trim() || ''
    if (tempShowTitle !== showTitle) newAttributes.showTitle = tempShowTitle
    if (tempIcon !== icon) newAttributes.icon = tempIcon
    if (tempIconType !== iconType) newAttributes.iconType = tempIconType
    if (tempIconCategory !== iconCategory)
      newAttributes.iconCategory = tempIconCategory
    if (tempIconColor !== iconColor) newAttributes.iconColor = tempIconColor

    if (tempShowIcon !== showIcon) {
      newAttributes.showIcon = tempShowIcon
      if (tempShowIcon) {
        if (!newAttributes.icon) newAttributes.icon = 'sparkles'
        if (!newAttributes.iconType) newAttributes.iconType = 'REGULAR'
        if (!newAttributes.iconCategory) newAttributes.iconCategory = 'LUCIDE'
        if (!newAttributes.iconColor) newAttributes.iconColor = '#3b82f6'
      }
    }

    if (tempCropStyle !== cropStyle) newAttributes.cropStyle = tempCropStyle

    if (tempPosition !== stepNumber) {
      const pos = getPos()
      if (pos !== undefined) {
        const $pos = editor.state.doc.resolve(pos)
        for (let depth = $pos.depth; depth > 0; depth--) {
          const stepsNode = $pos.node(depth)
          if (stepsNode.type.name !== 'Steps') continue

          const currentIndex = $pos.index(depth)
          const targetIndex = Math.max(
            0,
            Math.min(tempPosition - 1, stepsNode.childCount - 1)
          )
          const children: ProseMirrorNode[] = []
          stepsNode.forEach((child) => children.push(child))

          const [currentStep] = children.splice(currentIndex, 1)
          if (!currentStep) break
          const movedStep = currentStep.type.create(
            { ...currentStep.attrs, ...newAttributes },
            currentStep.content,
            currentStep.marks
          )
          children.splice(targetIndex, 0, movedStep)

          const reorderedSteps = children.map((child, index) =>
            child.type.name === 'Step'
              ? child.type.create(
                  { ...child.attrs, number: index + 1 },
                  child.content,
                  child.marks
                )
              : child
          )
          const contentStart = $pos.start(depth)
          editor.view.dispatch(
            editor.state.tr
              .replaceWith(
                contentStart,
                contentStart + stepsNode.content.size,
                Fragment.fromArray(reorderedSteps)
              )
              .scrollIntoView()
          )
          break
        }
      }
    } else if (Object.keys(newAttributes).length > 0) {
      updateAttributes(newAttributes)
    }
    setIsPopoverOpen(false)
  }, [
    tempTitle,
    stepTitle,
    tempGuideAction,
    guideAction,
    tempShowTitle,
    showTitle,
    tempIcon,
    icon,
    tempIconType,
    iconType,
    tempIconCategory,
    iconCategory,
    tempIconColor,
    iconColor,
    tempShowIcon,
    showIcon,
    tempCropStyle,
    cropStyle,
    tempPosition,
    stepNumber,
    getPos,
    editor,
    updateAttributes,
  ])

  // Function to cancel settings changes
  const cancelSettings = useCallback(() => {
    setTempTitle(stepTitle)
    setTempGuideAction(guideAction)
    setTempShowTitle(showTitle)
    setTempIcon(icon)
    setTempIconType(iconType)
    setTempIconCategory(iconCategory)
    setTempIconColor(iconColor)
    setTempShowIcon(showIcon)
    setTempCropStyle(cropStyle)
    setTempPosition(stepNumber)
    setTempScreenshotZoomLevel(attrs.screenshotZoomLevel || 1)
    setIsPopoverOpen(false)
  }, [
    stepTitle,
    guideAction,
    showTitle,
    icon,
    iconType,
    iconCategory,
    iconColor,
    showIcon,
    cropStyle,
    stepNumber,
    attrs.screenshotZoomLevel,
  ])

  // Function to open settings popover
  const openSettings = useCallback(() => {
    setTempTitle(stepTitle)
    setTempGuideAction(guideAction)
    setTempShowTitle(showTitle)
    setTempIcon(icon)
    setTempIconType(iconType)
    setTempIconCategory(iconCategory)
    setTempIconColor(iconColor)
    setTempShowIcon(showIcon)
    setTempCropStyle(cropStyle)
    setTempPosition(stepNumber)
    setTempScreenshotZoomLevel(attrs.screenshotZoomLevel || 1)
    setIsPopoverOpen(true)
  }, [
    stepTitle,
    guideAction,
    showTitle,
    icon,
    iconType,
    iconCategory,
    iconColor,
    showIcon,
    cropStyle,
    stepNumber,
    attrs.screenshotZoomLevel,
  ])

  // Function to handle deleting this step
  const handleDeleteStep = useCallback(() => {
    deleteNode()
  }, [deleteNode])

  // Function to start editing the title
  const handleTitleClick = useCallback(() => {
    if (editor.isEditable) {
      setIsEditingTitle(true)
      setEditingTitle(stepTitle)
    }
  }, [editor.isEditable, stepTitle])

  // Function to save the edited title
  const handleTitleSave = useCallback(() => {
    if (editingTitle.trim() !== stepTitle) {
      updateAttributes({ title: editingTitle.trim() || 'Step' })
    }
    setIsEditingTitle(false)
  }, [editingTitle, stepTitle, updateAttributes])

  // Function to cancel editing
  const handleTitleCancel = useCallback(() => {
    setEditingTitle(stepTitle)
    setIsEditingTitle(false)
  }, [stepTitle])

  // Function to handle key press in title input
  const handleTitleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        handleTitleSave()
      } else if (e.key === 'Escape') {
        e.preventDefault()
        handleTitleCancel()
      }
    },
    [handleTitleSave, handleTitleCancel]
  )

  const [fallbackStepId] = useState(() => nanoid(10))
  const stepId = attrs.id || fallbackStepId
  const canSelectForCopy = Boolean(attrs.id)

  // 获取全局已编辑截图映射
  const [editedMap] = useAtom(editedScreenshotMapAtom)
  // Fetch screenshot data only for likely DB IDs (avoid fetching for human-friendly IDs like "step-save-todo")
  // dE3G5MkCp6r_MG_IJ_fvu
  const isLikelyDbId = !!stepId && (stepId.length === 21 || stepId.startsWith('step-'))  // && /^[a-z0-9\\-_]{21,}$/.test(stepId);
  const shouldFetchStep = !isDetached && !!stepId && isLikelyDbId && cropStyle !== 'none'
  const { step: stepData, isLoading: isLoadingStep } = useScreenshotStepData({
    stepId: shouldFetchStep ? stepId : undefined,
    enabled: shouldFetchStep,
  })

  // 优先使用保存后的 editedUrl（包括后端返回的 editedUrl），其次节点属性上的 screenshotUrl，最后后端原始 url
  const screenshotUrl =
    editedMap[stepId]?.url ||
    stepData?.screenshot?.editedUrl ||
    attrs.screenshotUrl ||
    stepData?.screenshot?.url
  // Use viewportInfo dimensions for consistent aspect ratio with interaction points
  // This ensures the container matches the original capture viewport
  const screenshotWidth =
    attrs.screenshotWidth || stepData?.viewportInfo?.width || stepData?.screenshot?.width || 800
  const screenshotHeight =
    attrs.screenshotHeight || stepData?.viewportInfo?.height || stepData?.screenshot?.height || 600
  // 分享/只读页无 stepByIdApiResolver，stepData 不可得，回退到 attrs 中的持久化数据
  const elementBox =
    editedMap[stepId]?.viewportCoordinates ||
    stepData?.viewportCoordinates ||
    attrs.elementBox

  const interactionPoints: InteractionPoint[] = useMemo(() => {
    const viewportCoordinates =
      editedMap[stepId]?.viewportCoordinates || stepData?.viewportCoordinates

    if (stepData?.viewportInfo && viewportCoordinates) {
      // viewportInfo 尺寸是原始捕获尺寸，与截图容器基准一致
      const refWidth = stepData.viewportInfo.width
      const refHeight = stepData.viewportInfo.height

      return [
        {
          id: stepData.id,
          type: stepData.action || 'Click',
          x: viewportCoordinates.center.x,
          y: viewportCoordinates.center.y,
          width: viewportCoordinates.width,
          height: viewportCoordinates.height,
          xPercent: (viewportCoordinates.center.x / refWidth) * 100,
          yPercent: (viewportCoordinates.center.y / refHeight) * 100,
          widthPercent: (viewportCoordinates.width / refWidth) * 100,
          heightPercent: (viewportCoordinates.height / refHeight) * 100,
          label: getStepAction(stepData) ?? '',
          style: stepData.action,
        },
      ]
    }

    if (Array.isArray(attrs.interactionPoints) && attrs.interactionPoints.length > 0) {
      return attrs.interactionPoints as InteractionPoint[]
    }

    return []
  }, [stepData, editedMap, stepId, attrs.interactionPoints])

  // Update node attributes when screenshot data is loaded from database
  React.useEffect(() => {
    if (!stepData?.screenshot?.url || isLoadingStep || !editor.isEditable)
      return

    // Use setTimeout to ensure the update happens after the editor is ready
    const timeoutId = setTimeout(() => {
      const pos = getPos()
      if (pos === undefined) return

      const updates: Partial<StepAttributes> = {}
      let hasUpdates = false

      // Update screenshot URL if not already set (prefer editedUrl)
      const preferredUrl =
        stepData.screenshot.editedUrl || stepData.screenshot.url
      if (!attrs.screenshotUrl && preferredUrl) {
        updates.screenshotUrl = preferredUrl
        hasUpdates = true
      }

      // Update width if not already set
      if (stepData.screenshot.width && !attrs.screenshotWidth) {
        updates.screenshotWidth = stepData.screenshot.width
        hasUpdates = true
      }

      // Update height if not already set
      if (stepData.screenshot.height && !attrs.screenshotHeight) {
        updates.screenshotHeight = stepData.screenshot.height
        hasUpdates = true
      }

      // Update interaction points if not already set
      if (interactionPoints.length > 0 && !attrs.interactionPoints) {
        updates.interactionPoints = interactionPoints
        hasUpdates = true
      }

      // Update element box if not already set
      if (elementBox && !attrs.elementBox) {
        updates.elementBox = elementBox
        hasUpdates = true
      }

      // console.log('hasUpdates', updates, attrs)
      if (hasUpdates) {
        updateAttributes(updates)
      }
    }, 100)

    return () => clearTimeout(timeoutId)
  }, [
    stepData,
    isLoadingStep,
    attrs.screenshotUrl,
    attrs.screenshotWidth,
    attrs.screenshotHeight,
    attrs.interactionPoints,
    attrs.elementBox,
    interactionPoints,
    elementBox,
    updateAttributes,
    editor.isEditable,
    getPos,
  ])

  const stepColor = STEP_COLORS[(stepNumber - 1) % STEP_COLORS.length]

  const stepNumberColor = 'rgb(124, 144, 112)'
  const isSelectedForCopy = selectedStepIds.has(stepId)

  const toggleSelectedForCopy = useCallback(() => {
    setSelectedStepIds((current) => {
      const next = new Set(current)
      if (next.has(stepId)) next.delete(stepId)
      else next.add(stepId)
      return next
    })
  }, [setSelectedStepIds, stepId])

  return (
    <NodeViewWrapper
      className={cn(
        'group/step relative mb-8',
        selected && 'ProseMirror-selectednode',
        isSelectionMode &&
          !editor.isEditable &&
          canSelectForCopy &&
          'cursor-pointer rounded-xl',
        isSelectionMode && isSelectedForCopy && 'ring-2 ring-sidebar-primary ring-offset-4'
      )}
      data-type='Step'
      data-dg-id={stepId}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={
        isSelectionMode && !editor.isEditable && canSelectForCopy
          ? (event: React.MouseEvent) => {
              if ((event.target as HTMLElement).closest('button')) return
              event.preventDefault()
              event.stopPropagation()
              toggleSelectedForCopy()
            }
          : undefined
      }
    >
      {isSelectionMode && !editor.isEditable && canSelectForCopy && (
        <div
          className='absolute -left-10 top-1 z-20'
          contentEditable={false}
          onClick={(event) => event.stopPropagation()}
        >
          <Checkbox
            checked={isSelectedForCopy}
            onCheckedChange={toggleSelectedForCopy}
            aria-label={`Select step ${stepNumber}`}
            className='data-[state=checked]:border-sidebar-primary data-[state=checked]:bg-sidebar-primary'
          />
        </div>
      )}
      {/* Step content */}
      <div className='step-content relative flex flex-col gap-4'>
        {/* Step header with number and title */}
        <div className='flex items-center gap-3'>
          {/* Step number badge */}
          <div
            className='flex h-8 w-8 shrink-0 items-center justify-center rounded-md font-[Fraunces] text-base font-medium text-white'
            style={{ backgroundColor: stepNumberColor }}
          >
            {/* Action buttons - appears on hover (hidden in review mode) */}
            {editor.isEditable && !(editor as any).reviewMode && (isHovered || isPopoverOpen) ? (
              <div
                contentEditable={false}
                className={`pointer-events-auto z-10 flex h-full gap-0 transition-opacity ${isHovered || isPopoverOpen ? 'opacity-100' : 'opacity-0'
                  }`}
              >
                {/* Settings Popover */}
                <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant='ghost'
                      size='menuAction'
                      className='h-8 w-8 rounded-md text-white hover:bg-white/25'
                      onClick={openSettings}
                    >
                      <Pencil className='h-4 w-4' />
                    </Button>
                  </PopoverTrigger>

                  <PopoverContent
                    className='w-[340px] rounded-2xl border border-sidebar-border bg-white p-0 shadow-[0_8px_40px_0_rgba(0,0,0,0.08)]'
                    align='start'
                    side='bottom'
                    sideOffset={8}
                  >
                    <div className='flex flex-col'>
                      {/* Header */}
                      <div className='flex items-center justify-between border-b border-sidebar-border px-6 py-5'>
                        <h3 className='font-[Fraunces] text-[20px] leading-[1.2] font-medium text-sidebar-foreground'>
                          Step Settings
                        </h3>
                        <Button
                          variant='ghost'
                          size='menuAction'
                          onClick={cancelSettings}
                          className='h-8 w-8 text-[#86888F] hover:bg-muted hover:text-[#43464D]'
                        >
                          <X className='h-4 w-4' />
                        </Button>
                      </div>

                      <div className='space-y-4 px-6 py-6'>
                        {/* Show Title Toggle */}
                        <div className='flex items-center justify-between'>
                          <label className='text-[13px] leading-[1.2] font-normal text-[#86888F]'>
                            Show Title
                          </label>
                          <Switch
                            checked={tempShowTitle}
                            onCheckedChange={() => setTempShowTitle(!tempShowTitle)}
                          />
                        </div>

                        {/* Title Input */}
                        <div className='flex flex-col gap-2'>
                          <label className='text-[13px] leading-[1.2] font-normal text-[#1F2024]'>
                            Title
                          </label>
                          <div className='flex h-[42px] items-center gap-0'>
                            <div className='flex  w-11 shrink-0 items-center justify-between   bg-white'>
                              <EmojiPicker
                                selectedIcon={tempShowIcon ? tempIcon : undefined}
                                iconColor={tempIconColor}
                                iconCategory={tempIconCategory}
                                onEmojiSelect={(emoji) => {
                                  setTempShowIcon(true)
                                  setTempIcon(emoji)
                                  setTempIconCategory('EMOJI')
                                }}
                                onIconChange={(
                                  iconName: string,
                                  iconCategory: IconCategory
                                ) => {
                                  if (iconName === '') {
                                    setTempIconCategory('LUCIDE')
                                    setTempShowIcon(false)
                                    return
                                  }
                                  setTempShowIcon(true)
                                  setTempIcon(iconName)
                                  setTempIconCategory(iconCategory)
                                }}
                                onColorChange={setTempIconColor}
                                isPopoverMode={true}
                              />
                            </div>
                            <input
                              type='text'
                              value={tempTitle}
                              onChange={(e) => setTempTitle(e.target.value)}
                              placeholder='Enter step title...'
                              className='placeholder:text-[#85878D] focus-visible:ring-ring h-[36px] w-full rounded-md border border-[#E3E1DD]  px-3 text-[13px] leading-[1.2] font-normal text-[#43464D] transition-colors focus-visible:ring-1 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50'
                            />
                          </div>
                        </div>

                        <div className='flex flex-col gap-2'>
                          <label className='text-[13px] leading-[1.2] font-normal text-[#1F2024]'>
                            Position
                          </label>
                          <Select
                            value={String(tempPosition)}
                            onValueChange={(value) =>
                              setTempPosition(Number(value))
                            }
                          >
                            <SelectTrigger className='h-9 w-full rounded-md border-[#E3E1DD] text-[13px] text-[#43464D]'>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {Array.from({ length: totalSteps }, (_, index) => (
                                <SelectItem
                                  key={index + 1}
                                  value={String(index + 1)}
                                >
                                  Step {index + 1}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      {/* <div className='space-y-5'>
                        <div className='flex items-center justify-between'>
                          <label className='text-muted-foreground text-xs leading-4'>
                            Show Icon
                          </label>
                          <Switch
                            checked={tempShowIcon}
                            onCheckedChange={() => setTempShowIcon(!tempShowIcon)}
                          />
                        </div>

      s                  {tempShowIcon && (
                          <div onClick={(e) => e.stopPropagation()}>
                            <IconEditor
                              selectedIcon={tempIcon}
                              iconType={tempIconType}
                              iconCategory={tempIconCategory}
                              iconColor={tempIconColor}
                              onIconChange={setTempIcon}
                              onTypeChange={setTempIconType}
                              onCategoryChange={setTempIconCategory}
                              onColorChange={setTempIconColor}
                            />
                          </div>
                        )}
                      </div> */}
                      {/* <div className='flex flex-col gap-2'>
                        <label className='text-muted-foreground text-xs leading-4'>
                          Icon
                        </label>
                        <EmojiPicker
                          selectedIcon={tempShowIcon ? tempIcon : undefined}
                          iconColor={tempIconColor}
                          iconCategory={tempIconCategory}
                          onEmojiSelect={(emoji) => {
                            setTempShowIcon(true)
                            setTempIcon(emoji)
                            setTempIconCategory('EMOJI')
                          }}
                          onIconChange={(
                            iconName: string,
                            iconCategory: IconCategory
                          ) => {
                            if (iconName === '') {
                              setTempIconCategory('LUCIDE')
                              setTempShowIcon(false)
                              return
                            }
                            setTempShowIcon(true)
                            setTempIcon(iconName)
                            setTempIconCategory(iconCategory)
                          }}
                          onColorChange={setTempIconColor}
                          isPopoverMode={true}
                        />
                      </div> */}

                      {/* Screenshot Crop Style */}
                      {/* 暂时先注释 */}
                      {/* <div className='flex flex-col gap-2'>
                        <label className='text-muted-foreground text-xs leading-4'>
                          Screenshot Display
                        </label>
                        <Select
                          value={tempCropStyle}
                          onValueChange={(value: 'cropped' | 'full' | 'none') =>
                            setTempCropStyle(value)
                          }
                        >
                          <SelectTrigger className='h-9 w-full'>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value='cropped'>Cropped (Default)</SelectItem>
                            <SelectItem value='full'>Full Screenshot</SelectItem>
                            <SelectItem value='none'>No Screenshot</SelectItem>
                          </SelectContent>
                        </Select>
                      </div> */}

                      <Separator className='m-0 bg-sidebar-border' />

                      {/* Action Buttons */}
                      <div className='flex items-center justify-between  px-6 pt-[14px] pb-4'>
                        <Button
                          variant='destructive'
                          className='h-9 bg-transparent px-0 text-[13px] leading-[1.1] font-normal text-[#C24646] shadow-none hover:bg-transparent hover:text-[#C24646]'
                          size='sm'
                          onClick={handleDeleteStep}
                        >
                          Delete
                        </Button>
                        <div className='flex items-center space-x-2'>
                          <Button
                            variant='outline'
                            size='sm'
                            className='h-9 w-24 rounded-lg border-[#E3E1DD] px-0 text-[13px] text-[#43464D] hover:bg-muted'
                            onClick={cancelSettings}
                          >
                            Cancel
                          </Button>
                          <Button
                            size='sm'
                            className='h-9 w-[120px] rounded-lg bg-sidebar-primary px-0 text-[13px] text-white hover:bg-[#6f8264]'
                            onClick={saveSettings}
                          >
                            Save Changes
                          </Button>
                        </div>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            ) : stepNumber
            }
          </div>

          {/* Title and icon */}
          <div className='flex flex-1 items-center gap-2'>
              {showIcon && (
                <IconComponent
                  iconName={icon}
                  iconType={iconType}
                  iconCategory={iconCategory}
                  iconColor={iconColor}
                  size={20}
                  className={`transition-colors ${
                    iconCategory === 'EMOJI' ? 'h-[20px] text-[20px]' : ''
                  }`}
                />
              )}
              {(showTitle || stepTitle) && (
                <>
                  {isEditingTitle ? (
                    <input
                      type='text'
                      value={editingTitle}
                      onChange={(e) => setEditingTitle(e.target.value)}
                      onBlur={handleTitleSave}
                      onKeyDown={handleTitleKeyDown}
                      autoFocus
                      className='w-full border-none bg-transparent font-[Fraunces] text-lg font-medium text-sidebar-foreground outline-none'
                      placeholder='Enter step title...'
                    />
                  ) : (
                    <h3
                      className='hover:bg-accent -mx-1 !mb-0 !mt-0 cursor-pointer rounded px-1 font-[Fraunces] text-lg font-medium text-sidebar-foreground transition-colors'
                      onClick={handleTitleClick}
                      contentEditable={false}
                   
                    >
                      {stepTitle || 'Click to add title'}
                    </h3>
                  )}
                </>
              )}
          </div>
        </div>

            {/* Step body content */}
            <div className="step-content-body text-[15px] text-muted-foreground leading-[1.5] font-['Plus_Jakarta_Sans']">
              <NodeViewContent />
            </div>

            {/* Screenshot shimmer placeholder while loading */}
            {!screenshotUrl && isLoadingStep && cropStyle !== 'none' && (
              <div className='relative overflow-hidden rounded-xl border border-sidebar-border bg-sidebar-border' style={{ aspectRatio: '16 / 10' }}>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/50 to-transparent animate-[shimmer_1.5s_ease-in-out_infinite]" style={{ backgroundSize: '200% 100%' }} />
              </div>
            )}

            {/* Screenshot */}
            {screenshotUrl && (
              <div className='group/media relative w-full overflow-visible rounded-xl border border-sidebar-border'>
                <ScreenshotRenderer
                  url={screenshotUrl}
                  width={screenshotWidth}
                  height={screenshotHeight}
                  elementBox={elementBox}
                  interactionPoints={interactionPoints}
                  cropStyle={cropStyle}
                  align={attrs.align || 'center'}
                  alt={`Screenshot for ${stepTitle || 'step'}`}
                  zoomLevel={attrs.screenshotZoomLevel || 1}
                  panX={attrs.screenshotPanX ?? 0}
                  panY={attrs.screenshotPanY ?? 0}
                  onZoomChange={(zoom) => {
                    updateAttributes({
                      screenshotZoomLevel: zoom,
                      ...(zoom <= 1
                        ? { screenshotPanX: 0, screenshotPanY: 0 }
                        : {}),
                    })
                  }}
                  onAlignChange={(align) => updateAttributes({ align })}
                  onWidthChange={(newWidth) => {
                    const newHeight =
                      (newWidth / screenshotWidth) * screenshotHeight
                    updateAttributes({
                      screenshotWidth: newWidth,
                      screenshotHeight: newHeight,
                    })
                  }}
                  onPanChange={editor.isEditable
                    ? (panX, panY) => {
                        updateAttributes({
                          screenshotPanX: panX,
                          screenshotPanY: panY,
                        })
                      }
                    : undefined}
                  readOnly={!editor.isEditable}
                  isFullScreenshot={true}
                />
                <ScreenshotToolbar
                  zoomLevel={attrs.screenshotZoomLevel || 1}
                  onZoomChange={(zoom) => {
                    updateAttributes({
                      screenshotZoomLevel: zoom,
                      ...(zoom <= 1
                        ? { screenshotPanX: 0, screenshotPanY: 0 }
                        : {}),
                    })
                  }}
                  onDelete={() => {
                    updateAttributes({
                      screenshotUrl: null,
                      screenshotWidth: null,
                      screenshotHeight: null,
                      cropStyle: 'none',
                    })
                  }}
                  interactionPoints={interactionPoints}
                  src={screenshotUrl}
                  stepId={attrs.id || stepId}
                  canModify={editor.isEditable}
                  persistToRecordedStep={!isDetached}
                  onImageSaved={(url, viewportCoordinates) => {
                    updateAttributes({
                      screenshotUrl: url,
                      ...(viewportCoordinates
                        ? { elementBox: viewportCoordinates }
                        : {}),
                    })
                  }}
                />
              </div>
            )}

            {/* Caption below screenshot (PDF-only) */}
            {screenshotUrl && showCaption && captionText && (
              <div
                className='text-center text-sm italic text-gray-600'
                contentEditable={false}
              >
                <span className='font-semibold text-gray-900'>Step {stepNumber} —</span> {captionText}
              </div>
            )}

            {/* Guide action callout */}
            {guideAction && (
              <div
                className="flex items-center gap-2 rounded-md px-4 py-3"
                style={{
                  backgroundColor: `${stepColor}10`,
                  borderLeft: `3px solid ${stepColor}`,
                }}
                contentEditable={false}
              >
                <MousePointerClick className="h-4 w-4 shrink-0" style={{ color: stepColor }} />
                <span className="font-['Plus_Jakarta_Sans'] text-sm font-semibold text-sidebar-foreground">
                  {guideAction}
                </span>
              </div>
            )}

      </div>
    </NodeViewWrapper>
  )
}
